import React, { Component, Fragment } from 'react';
import { CustomInput, Label } from 'reactstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { gql } from 'apollo-boost';
import { Query } from 'react-apollo';
import { Card } from 'reactstrap';
import moment from 'moment';
import { remove, find } from 'lodash';
import IconTooltip from '~/components/iconTooltip/iconTooltip';
import Spinner from '~/components/spinner/spinner';
import LotStatusIcon from '~/components/lotStatusIcon/lotStatusIcon';
import LotStatusText from '~/components/lotStatusText/lotStatusText';
import FilterInput from '~/components/filterInput/filterInput';
import LotListActions from '~/containers/lotListActions/lotListActions';
import URLParamBanner from '~/components/urlParamBanner/urlParamBanner';
import ErrorBoundaryContent from '~/components/errorBoundary/content';
import styles from './styles.css';

export const getLotsQuery = gql`
  query lots($breweryId: Int!, $isConsolidated: Boolean, $status: String) {
    lots(breweryId: $breweryId, isConsolidated: $isConsolidated, status: $status) {
      id
      reference
      startDate
      createdAt
      volume
      status
      dutyPaid
      isSample
      product {
        id
        name
      }
      nextTransfer {
        id
        transferDate
        name
        type
        tankType
        tank {
          id
          name
        }
      }
    }
  }
`;

export class LotsList extends Component {
  state = {
    selectedLots: [],
    filteredLots: [],
    filterText: ''
  };

  onHeaderCheck = (event, lots) => {
    event.preventDefault();
    const { selectedLots, filteredLots } = this.state;
    if (selectedLots.length) {
      return this.setState({
        selectedLots: []
      });
    }

    if (filteredLots.length) {
      return this.setState({
        selectedLots: filteredLots
      });
    }

    return this.setState({
      selectedLots: lots
    });
  };

  onLotCheck = (event, lot) => {
    event.stopPropagation();

    const { selectedLots } = this.state;
    if (!!find(selectedLots, l => l.id === lot.id)) {
      remove(selectedLots, l => l.id === lot.id);
    } else {
      selectedLots.push(lot);
    }

    this.setState({
      selectedLots: [].concat(selectedLots)
    });
  };

  onFilterChange = (event, lots) => {
    const { value } = event.target;
    if (!value.length) {
      return this.setState({
        filteredLots: [],
        filterText: ''
      });
    }
    const filterValue = value.toLowerCase();

    const filteredLots = lots.filter(lot => {
      return (
        lot.reference.toLowerCase().indexOf(filterValue) !== -1 ||
        lot.product.name.toLowerCase().indexOf(filterValue) !== -1 ||
        lot.status
          .replace('_', ' ')
          .toLowerCase()
          .indexOf(filterValue) !== -1
      );
    });

    return this.setState({
      filterText: value,
      filteredLots,
      selectedLots: []
    });
  };

  deselectLots = () => {
    this.setState({
      selectedLots: []
    });
  };

  renderHeaderCheckbox = lots => {
    const { selectedLots, filteredLots } = this.state;
    let checked = false;
    if (filteredLots.length) {
      checked = filteredLots.length === selectedLots.length;
    } else {
      checked = lots.length === selectedLots.length;
    }
    return (
      <Label onClick={e => this.onHeaderCheck(e, lots)}>
        <CustomInput
          readOnly
          id="headerCheck"
          checked={checked}
          type="checkbox"
          className={`${styles.headerCheckbox} center`}
        />
      </Label>
    );
  };

  renderNextTransferDate = lot => {
    const { nextTransfer } = lot;
    if (!nextTransfer) {
      return '--';
    }

    const transferDate = moment(nextTransfer.transferDate);
    let extraInfoFragment = null;

    if (transferDate.startOf('d') === moment().startOf('d')) {
      extraInfoFragment = <span className={`db ${styles.dueToday}`}>(due today)</span>;
    } else if (transferDate.toDate() < new Date()) {
      extraInfoFragment = <span className={`db ${styles.overdue}`}>(overdue)</span>;
    }

    return (
      <Fragment>
        {extraInfoFragment}
        <span className="db strong">
          {nextTransfer.name}
          {nextTransfer.type === 'TANK' &&
            ['BRITE_TANK', 'UNI_TANK'].indexOf(nextTransfer.tankType) !== -1 &&
            nextTransfer.tank && <em> - {nextTransfer.tank.name}</em>}
        </span>

        <span className="db">{transferDate.format('dddd Do MMMM')}</span>
      </Fragment>
    );
  };

  render() {
    const { brewery, history, status } = this.props;
    return (
      <Query
        query={getLotsQuery}
        variables={{ breweryId: brewery.id, isConsolidated: false, status }}
        fetchPolicy={'cache-and-network'}
      >
        {props => {
          const { loading, error, data } = props;
          if (loading) {
            return <Spinner className="absolute absolute-center stroke-brand-red" />;
          }

          if (error) {
            return <ErrorBoundaryContent error={error} />;
          }

          const { filterText, filteredLots, selectedLots } = this.state;
          const lots = filterText.length ? filteredLots : data.lots;
          const sortedLots = lots.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          return (
            <Fragment>
              <URLParamBanner addWord="scheduled" name="lot" />
              <Card className="content">
                <div className="flex pa3 w-100">
                  <FilterInput onChange={e => this.onFilterChange(e, data.lots)} className="flex-grow-1" />
                  <LotListActions selectedLots={selectedLots} refreshList={this.deselectLots} />
                </div>
                <div>
                  <header className="flex ttu table-header pv2">
                    <div className="w-5 tc">{this.renderHeaderCheckbox(data.lots)}</div>
                    <div className="w-20">Status</div>
                    <div className="w-10">Lot #</div>
                    <div className="w-20">Product</div>
                    <div className="w-15">Start date</div>
                    <div className="w-20">Next transfer</div>
                    <div className="w-10">Volume</div>
                  </header>
                  <div>
                    {sortedLots.map(lot => (
                      <div
                        className="flex pointer pv3 bt-divider row-hover items-center"
                        key={lot.id}
                        onClick={() => {
                          history.push(`/lots/${lot.id}`);
                        }}
                      >
                        <div className="w-5 pl2 pr2 tc" onClick={e => this.onLotCheck(e, lot)}>
                          <Label>
                            <CustomInput
                              readOnly
                              type="checkbox"
                              checked={!!find(selectedLots, l => l.id === lot.id)}
                              className={`${styles.checkbox} center`}
                            />
                          </Label>
                        </div>
                        <div className="w-20 pr2 flex items-center">
                          <LotStatusIcon className="mr2" status={lot.status} />
                          <LotStatusText capitalise status={lot.status} className="flex-grow-1" />
                          {lot.dutyPaid && (
                            <IconTooltip text="Duty paid" icon="money-bag-dollar" className={`${styles.icon} ml1`} />
                          )}
                          {lot.isSample && (
                            <IconTooltip text="A sample lot" icon="lab-tube-bottle" className={`${styles.icon} ml1`} />
                          )}
                        </div>

                        <div className="w-10 pr2">
                          <strong>{lot.reference}</strong>
                        </div>
                        <div className="w-20 pr2">{lot.product.name}</div>
                        <div className="w-15 pr2">{moment(lot.startDate).format('dd Do MM')}</div>
                        <div className="w-20 pr2">{lot.status !== 'COMPLETED' && this.renderNextTransferDate(lot)}</div>
                        <div className="w-10">{lot.volume}L</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </Fragment>
          );
        }}
      </Query>
    );
  }
}

function mapStateToProps(state) {
  return {
    brewery: state.brewery
  };
}

export default connect(mapStateToProps)(withRouter(LotsList));



// WEBPACK FOOTER //
// ./src/containers/lotList/lotList.jsx